import { ThemePalette } from 'mui.theme';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatYAxis } from '../Individual/Chart';
import {
  darkThemeLineChartDefaultColorMap,
  lineChartDefaultColorMap,
} from 'utils/helpers/general';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import {
  DATE_TIME_FORMAT,
  DATE_TIME_FORMAT_SECONDS,
} from 'utils/constants/dateTime';
import useAppTheme from 'hooks/useAppTheme';
import { useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

export const GroupedLineChart = ({
  enableDarkTheme,
  dataType,
  data,
  uniqueUnits,
  selectedOperation,
}: {
  enableDarkTheme: boolean;
  dataType: string;
  data: any;
  uniqueUnits: string[];
  exportTo: any;
  unitSystem: string;
  handleExport: any;
  selectedAsset: any;
  startDate: any;
  endDate: any;
  activeButton: any;
  selectedOperation: string;
}) => {
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const formatTimestamp = (timestamp: number): string => {
    // const date = dayjs(timestamp);
    // let formattedDate = date.format('YY MMM DD');
    // if (dataType === 'highGrain') {
    //   formattedDate = date.format('HH:mm:ss');
    // }
    // return formattedDate;
    return convertUtcToDisplayTime(
      timestamp,
      preferredTimeDisplayMode,
      timezone,
      dataType === 'highGrain' ? 'HH:mm:ss' : 'YY MMM DD'
    );
  };

  const formatTimestampWithHour = (timestamp: number): string => {
    // const date = dayjs(timestamp);
    // const formattedDate = date.format('YYYY MMM DD HH:mm (Z)');
    // return formattedDate;
    return convertUtcToDisplayTime(
      timestamp,
      preferredTimeDisplayMode,
      timezone,
      DATE_TIME_FORMAT_SECONDS
    );
  };

  const formatValue = (value: any) => {
    if (typeof value === 'number' && value % 1 !== 0) {
      return value.toFixed(2);
    }
    return value;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        width={500}
        height={300}
        margin={{
          top: 30,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={
            enableDarkTheme
              ? _palette.dark.borderGrey
              : _palette.light.borderGrey
          }
          vertical={false}
        />
        <XAxis
          dataKey="ts"
          allowDuplicatedCategory={false}
          tickFormatter={formatTimestamp}
          textAnchor="end"
          tick={{ fontSize: 11 }}
        />

        {uniqueUnits.map((unit, index) => {
          return (
            <YAxis
              key={index}
              yAxisId={unit}
              stroke={
                enableDarkTheme
                  ? darkThemeLineChartDefaultColorMap[index]
                  : lineChartDefaultColorMap[index]
              }
              label={{
                value: `${unit}`,
                angle: -90,
                position: 'inside',
                fill: enableDarkTheme
                  ? ThemePalette.typography.wheat
                  : ThemePalette.typography.black,
                dy: 10,
                dx: 15,
                fontSize: 10,
              }}
              tickCount={8}
              tickFormatter={formatYAxis}
            />
          );
        })}
        <Tooltip
          cursor={{
            fill: enableDarkTheme
              ? alpha(_palette.dark.primary, 0.1)
              : 'rgba(0,0,0,0.05)',
          }}
          wrapperStyle={{ outline: 'none', fontSize: '11px' }}
          contentStyle={{
            backgroundColor: enableDarkTheme ? '#2D3748' : '#FFFFFF',
          }}
          itemStyle={{
            color: enableDarkTheme
              ? ThemePalette.typography.wheat
              : ThemePalette.typography.black,
          }}
          labelFormatter={(value) => formatTimestampWithHour(value)}
          formatter={(value) => formatYAxis(formatValue(value))}
        />
        <Legend
          wrapperStyle={{ fontSize: '20px' }}
          layout="horizontal"
          verticalAlign="top"
          align="center"
        />
        {data.map((item: any, index: number) => {
          return (
            <Line
              key={index}
              yAxisId={item.unit}
              type="monotone"
              dataKey={selectedOperation}
              data={item.data}
              stroke={
                enableDarkTheme
                  ? darkThemeLineChartDefaultColorMap[index]
                  : lineChartDefaultColorMap[index]
              }
              name={item.name}
              connectNulls={false}
              activeDot={{ r: 3 }}
              dot={{
                fill: enableDarkTheme
                  ? _palette.dark.boxBackground
                  : _palette.light.boxBackground,
                strokeWidth: 1,
              }}
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
};

// upper one will be removed after merging
export const GroupedLineChartNew = ({
  data,
  uniqueUnits,
  selectedOperation,
}: {
  data: any[];
  uniqueUnits: string[];
  selectedOperation: string;
}) => {
  const { enableDarkTheme } = useAppTheme();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const chartColors = enableDarkTheme
    ? darkThemeLineChartDefaultColorMap
    : lineChartDefaultColorMap;

  const getColor = (index: number) => chartColors[index % chartColors.length];

  const formatTimestamp = useMemo(
    () => (timestamp: number) =>
      convertUtcToDisplayTime(
        timestamp,
        preferredTimeDisplayMode,
        timezone,
        DATE_TIME_FORMAT
      ),
    [preferredTimeDisplayMode, timezone]
  );

  const formatTimestampWithHour = useMemo(
    () => (timestamp: number) =>
      convertUtcToDisplayTime(
        timestamp,
        preferredTimeDisplayMode,
        timezone,
        DATE_TIME_FORMAT_SECONDS
      ),
    [preferredTimeDisplayMode, timezone]
  );

  const formatValue = (value: any) =>
    typeof value === 'number' && value % 1 !== 0 ? value.toFixed(2) : value;

  const formatYAxis = (val: any) => formatValue(val);

  const textColor = enableDarkTheme
    ? ThemePalette.typography.wheat
    : ThemePalette.typography.black;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        width={500}
        height={300}
        margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={
            enableDarkTheme
              ? _palette.dark.borderGrey
              : _palette.light.borderGrey
          }
          vertical={false}
        />

        <XAxis
          dataKey="ts"
          allowDuplicatedCategory={false}
          tickFormatter={formatTimestamp}
          textAnchor="middle"
          tick={{
            fontSize: 11,
            fill: textColor,
          }}
        />

        {uniqueUnits.map((unit, index) => (
          <YAxis
            key={unit}
            yAxisId={unit}
            stroke={getColor(index)}
            label={{
              value: unit,
              angle: -90,
              position: 'inside',
              fill: textColor,
              dy: 10,
              dx: 15,
              fontSize: 10,
            }}
            tickCount={8}
            tickFormatter={formatYAxis}
            tick={{
              fontSize: 11,
            }}
          />
        ))}

        <Tooltip
          cursor={{
            fill: enableDarkTheme
              ? alpha(_palette.dark.primary, 0.1)
              : 'rgba(0,0,0,0.05)',
          }}
          wrapperStyle={{ outline: 'none', fontSize: '11px' }}
          contentStyle={{
            backgroundColor: enableDarkTheme ? '#2D3748' : '#FFFFFF',
          }}
          itemStyle={{
            color: textColor,
          }}
          labelStyle={{
            color: textColor,
          }}
          labelFormatter={formatTimestampWithHour}
          formatter={(value) => formatYAxis(formatValue(value))}
        />

        <Legend
          layout="horizontal"
          verticalAlign="top"
          iconType="circle"
          align="left"
          wrapperStyle={{
            paddingBottom: '28px',
            fontSize: '12px',
            textTransform: 'capitalize',
          }}
          fontSize={'10px'}
          formatter={(value: string) => {
            return (
              <>
                {typeof value === 'string' ? value?.split('_').join(' ') : ''}
              </>
            );
          }}
        />

        {data.map((item, index) => (
          <Line
            key={item.name}
            yAxisId={item.unit}
            type="monotone"
            dataKey={selectedOperation}
            data={item.data}
            stroke={getColor(index)}
            name={item.name}
            connectNulls={false}
            activeDot={{ r: 3 }}
            dot={{
              fill: enableDarkTheme
                ? _palette.dark.boxBackground
                : _palette.light.boxBackground,
              strokeWidth: 1,
            }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
